import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Drawer,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Row,
  Select,
  Skeleton,
  Space,
  Switch,
  Tabs,
  Tag,
  Typography
} from "antd";
import { MinusCircleOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../../commons/GeneralErrorAlert";
import { Success } from "../../../../commons/Messages/Messages";
import { isAuthenticated } from "../../../../commons/Auth/auth";
import { useIsCompact, useIsMobile } from "../../../../design-system/useMediaQuery";
import Amount from "../shared/Amount";
import MoneyInput from "../shared/MoneyInput";
import { formatCents } from "../shared/money";
import BelegPreview from "./BelegPreview";
import CreditPanel from "./CreditPanel";
import SelfBillingModal from "./SelfBillingModal";
import { BelegSubcontractorAlert } from "../../subcontractors/ComplianceTag";
import {
  approveBeleg,
  bookBeleg,
  createSupplier,
  getBeleg,
  listProjects,
  listSuppliers,
  reextractBeleg,
  rejectBeleg,
  updateBeleg
} from "./api";
import {
  CATEGORY_LABELS,
  KIND_LABELS,
  LOCKED_STATUSES,
  MISSING_LABELS,
  STATUS_COLORS,
  STATUS_LABELS,
  TAX_MODE_LABELS
} from "./constants";

const DATE_FIELDS = ["invoiceDate", "deliveryDate", "dueDate"];
const RATES = [19, 7, 0];
const CREATE = "__create__";

const toForm = beleg => {
  const values = {
    ...beleg,
    supplier: beleg.supplier?._id || beleg.supplier || undefined,
    costCentre: beleg.costCentre?._id || beleg.costCentre || undefined,
    byRate: (beleg.amounts?.byRate || []).map(g => ({ rate: g.rate, net: g.net, tax: g.tax })),
    skontoPercent: beleg.skonto?.percent || 0,
    skontoDays: beleg.skonto?.days || 0
  };
  DATE_FIELDS.forEach(f => {
    values[f] = beleg[f] ? dayjs(beleg[f]) : null;
  });
  if (!values.byRate.length) values.byRate = [{ rate: 19, net: 0, tax: 0 }];
  return values;
};

const fromForm = values => ({
  kind: values.kind,
  supplier: values.supplier === CREATE ? null : values.supplier || null,
  supplierName: values.supplierName,
  supplierAddress: values.supplierAddress,
  supplierVatId: values.supplierVatId,
  supplierTaxNumber: values.supplierTaxNumber,
  supplierIban: values.supplierIban,
  invoiceNumber: values.invoiceNumber,
  referencedInvoiceNumber: values.referencedInvoiceNumber || "",
  invoiceDate: values.invoiceDate ? values.invoiceDate.format("YYYY-MM-DD") : "",
  deliveryDate: values.deliveryDate ? values.deliveryDate.format("YYYY-MM-DD") : "",
  dueDate: values.dueDate ? values.dueDate.format("YYYY-MM-DD") : "",
  byRate: (values.byRate || []).map(g => ({ rate: g.rate, net: g.net || 0, tax: g.tax ?? null })),
  taxMode: values.taxMode,
  category: values.category || "",
  account: values.account || "",
  costCentre: values.costCentre || null,
  privateSharePercent: values.privateSharePercent || 0,
  isAsset: Boolean(values.isAsset),
  skonto: { percent: values.skontoPercent || 0, days: values.skontoDays || 0 },
  purpose: values.purpose,
  notes: values.notes
});

/** Desktop: file and form side by side. Below 992px: form first, the file in a second tab. */
const renderBody = ({ isCompact, preview, details, t }) =>
  isCompact ? (
    <Tabs
      items={[
        { key: "details", label: t("Details"), children: details },
        { key: "preview", label: t("Preview"), children: preview }
      ]}
    />
  ) : (
    <div className="acct-split">
      {preview}
      {details}
    </div>
  );

/**
 * Review form for one Beleg: file and fields side by side on desktop; below 992px the file
 * moves into a "Preview" tab next to the form.
 */
const BelegDrawer = ({ belegId, open, onClose, onChanged, meta, canEdit, canBook }) => {
  const { t, i18n } = useTranslation("accounting");
  const isMobile = useIsMobile();
  const isCompact = useIsCompact();
  // Phones get full-size (touch) controls; the dense small form stays on larger screens.
  const fieldSize = isMobile ? "middle" : "small";
  const [form] = Form.useForm();
  const [beleg, setBeleg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState("");
  const [acknowledge, setAcknowledge] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState("");
  const [projects, setProjects] = useState([]);
  const taxMode = Form.useWatch("taxMode", form);
  const category = Form.useWatch("category", form);
  const byRate = Form.useWatch("byRate", form) || [];
  const isAsset = Form.useWatch("isAsset", form);
  const kind = Form.useWatch("kind", form);
  const [selfBillingOpen, setSelfBillingOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getBeleg(belegId);
      setBeleg(res.data.beleg);
      form.setFieldsValue(toForm(res.data.beleg));
      setAcknowledge(Boolean(res.data.beleg.compliance?.acknowledgedBy));
    } catch (err) {
      generalErrorAlert(err);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !belegId) return;
    form.resetFields();
    load();
    listSuppliers({ limit: 500 })
      .then(res => setSuppliers(res.data.suppliers))
      .catch(() => {});
    listProjects(isAuthenticated()?.serviceProviderId)
      .then(res => setProjects(res.data.data || []))
      .catch(() => {});
  }, [open, belegId]);

  // Still being read: poll until the extraction finishes.
  useEffect(() => {
    if (!open || beleg?.extraction?.status !== "processing") return undefined;
    const timer = setTimeout(load, 2500);
    return () => clearTimeout(timer);
  }, [open, beleg]);

  const isSelfBilling = beleg?.kind === "self_billing";
  // A self-billing Gutschrift is edited in its own dialog, never in this form.
  const locked =
    !beleg ||
    LOCKED_STATUSES.includes(beleg.status) ||
    beleg.status === "rejected" ||
    isSelfBilling ||
    !canEdit;
  const processing = beleg?.extraction?.status === "processing";
  const uncertain = new Set(beleg?.extraction?.uncertainFields || []);
  const check = name =>
    uncertain.has(name) ? { validateStatus: "warning", help: t("Please check this value") } : {};

  const categoryOptions = useMemo(
    () =>
      (meta?.categories || []).map(c => ({
        value: c.key,
        label: t(CATEGORY_LABELS[c.key] || c.label)
      })),
    [meta, i18n.language]
  );

  /** Account follows the category, respecting 7 % materials and § 13b subcontractors. */
  const accountFor = (key, mode, groups) => {
    const c = (meta?.categories || []).find(x => x.key === key);
    if (!c) return "";
    if (mode === "reverse_charge" && c.accountReverseCharge) return c.accountReverseCharge;
    const mainRate = [...groups].sort((a, b) => (b.net || 0) - (a.net || 0))[0]?.rate;
    if (Number(mainRate) === 7 && c.accountReduced) return c.accountReduced;
    return c.account;
  };

  const onCategoryChange = key => {
    const c = (meta?.categories || []).find(x => x.key === key);
    form.setFieldsValue({
      account: accountFor(key, taxMode, byRate),
      isAsset: Boolean(c?.isAsset)
    });
  };

  const onRateGroupChange = (index, field, value) => {
    const groups = form.getFieldValue("byRate") || [];
    const g = { ...groups[index], [field]: value };
    if (field === "net" || field === "rate")
      g.tax = taxMode === "regular" ? Math.round(((g.net || 0) * Number(g.rate)) / 100) : 0;
    groups[index] = g;
    form.setFieldsValue({ byRate: [...groups] });
  };

  const totals = byRate.reduce(
    (s, g) => ({ net: s.net + (g?.net || 0), tax: s.tax + (g?.tax || 0) }),
    { net: 0, tax: 0 }
  );

  const supplierOptions = useMemo(() => {
    const list = suppliers.map(s => ({ value: s._id, label: `${s.name} (${s.supplierNumber})` }));
    const q = supplierSearch.trim();
    if (q && !suppliers.some(s => s.name.toLowerCase() === q.toLowerCase())) {
      list.unshift({ value: CREATE, label: `+ ${t("Create supplier")} "${q}"` });
    }
    return list;
  }, [suppliers, supplierSearch]);

  const onSupplierChange = async value => {
    if (value !== CREATE) {
      const s = suppliers.find(x => x._id === value);
      if (s && !form.getFieldValue("supplierName")) form.setFieldsValue({ supplierName: s.name });
      if (s?.defaultCategory && !form.getFieldValue("category")) {
        form.setFieldsValue({ category: s.defaultCategory });
        onCategoryChange(s.defaultCategory);
      }
      return;
    }
    try {
      const res = await createSupplier({
        name: supplierSearch.trim(),
        vatId: form.getFieldValue("supplierVatId"),
        taxNumber: form.getFieldValue("supplierTaxNumber"),
        address: { street: form.getFieldValue("supplierAddress") },
        ibans: form.getFieldValue("supplierIban")
          ? [{ iban: form.getFieldValue("supplierIban") }]
          : []
      });
      const s = res.data.supplier;
      setSuppliers(prev => [...prev, s]);
      form.setFieldsValue({
        supplier: s._id,
        supplierName: form.getFieldValue("supplierName") || s.name
      });
      Success(t("Supplier created"));
    } catch (err) {
      generalErrorAlert(err);
      form.setFieldsValue({ supplier: undefined });
    }
  };

  const run = async (kind, action, successText) => {
    setSaving(kind);
    try {
      const res = await action();
      const updated = res?.data?.beleg;
      if (updated) {
        setBeleg(updated);
        form.setFieldsValue(toForm(updated));
      }
      if (successText) Success(successText);
      onChanged?.(updated || null);
      return true;
    } catch (err) {
      generalErrorAlert(err);
      return false;
    } finally {
      setSaving("");
    }
  };

  const save = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return false;
    }
    return run("save", () => updateBeleg(belegId, fromForm(values)), t("Beleg saved"));
  };

  const approve = async () => {
    if (!locked && !(await save())) return;
    await run("approve", () => approveBeleg(belegId, { acknowledge }), t("Beleg approved"));
  };

  const book = async () => {
    if (!locked && !(await save())) return;
    const ok = await run("book", () => bookBeleg(belegId, { acknowledge }), t("Beleg booked"));
    if (ok) onClose();
  };

  const reject = () =>
    run("reject", () => rejectBeleg(belegId, {}), t("Beleg rejected")).then(ok => ok && onClose());

  const reextract = () =>
    run(
      "reextract",
      () => reextractBeleg(belegId, (i18n.language || "de").slice(0, 2)),
      t("The document is being read again.")
    );

  const compliance = beleg?.compliance;
  const extraction = beleg?.extraction;
  const journal = beleg?.journalEntry;

  const actions = !locked ? (
    <div className="acct-actions" style={{ justifyContent: "flex-end" }}>
      <Button
        icon={<ReloadOutlined />}
        onClick={reextract}
        loading={saving === "reextract"}
        disabled={processing}
      >
        {t("Re-extract")}
      </Button>
      <Popconfirm title={t("Reject this Beleg?")} onConfirm={reject}>
        <Button danger loading={saving === "reject"}>
          {t("Reject")}
        </Button>
      </Popconfirm>
      <Button onClick={save} loading={saving === "save"}>
        {t("Save")}
      </Button>
      {beleg?.status !== "approved" && (
        <Button onClick={approve} loading={saving === "approve"} disabled={processing}>
          {t("Approve")}
        </Button>
      )}
      {canBook && (
        <Button type="primary" onClick={book} loading={saving === "book"} disabled={processing}>
          {t("Book")}
        </Button>
      )}
    </div>
  ) : null;

  const draftActions =
    isSelfBilling && beleg?.status === "draft" && canEdit ? (
      <div className="acct-actions" style={{ justifyContent: "flex-end" }}>
        <Popconfirm title={t("Discard this draft?")} onConfirm={reject}>
          <Button danger loading={saving === "reject"}>
            {t("Discard")}
          </Button>
        </Popconfirm>
        <Button type="primary" onClick={() => setSelfBillingOpen(true)}>
          {t("Edit and issue")}
        </Button>
      </div>
    ) : null;

  const onCreditChanged = updated => {
    if (updated) {
      setBeleg(updated);
      form.setFieldsValue(toForm(updated));
    } else load();
    onChanged?.(updated || null);
  };

  const preview = (
    <BelegPreview belegId={belegId} mimeType={beleg?.mimeType} fileName={beleg?.originalName} />
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      rootClassName="acct-module"
      width={isMobile ? "100%" : 1200}
      destroyOnClose
      title={
        beleg ? (
          <Space wrap>
            <span className="acct-wrap">
              {beleg.belegNumber || beleg.invoiceNumber || beleg.originalName}
            </span>
            <Tag color={STATUS_COLORS[beleg.status]}>{t(STATUS_LABELS[beleg.status])}</Tag>
            {beleg.einvoice?.format && <Tag color="purple">{t("E-invoice")}</Tag>}
            {extraction?.confidence !== null &&
              extraction?.confidence !== undefined &&
              extraction.status === "done" && (
                <Tag>
                  {t("Confidence")}: {Math.round(extraction.confidence * 100)} %
                </Tag>
              )}
          </Space>
        ) : (
          t("Beleg")
        )
      }
      footer={actions || draftActions}
    >
      {loading && !beleg ? (
        <Skeleton active paragraph={{ rows: 12 }} />
      ) : (
        renderBody({
          isCompact,
          preview,
          t,
          details: (
            <div>
              {processing && (
                <Alert
                  type="info"
                  showIcon
                  className="mb-3"
                  message={t("The document is being read…")}
                />
              )}
              {extraction?.status === "failed" && (
                <Alert
                  type="error"
                  showIcon
                  className="mb-3"
                  message={t("The document could not be read")}
                  description={extraction.error}
                />
              )}
              {extraction?.status === "done" && extraction.warnings?.length > 0 && (
                <Alert
                  type="warning"
                  showIcon
                  className="mb-3"
                  message={t("Please check")}
                  description={
                    <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                      {extraction.warnings.map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  }
                />
              )}
              {beleg?.duplicateOf && (
                <Alert
                  type="warning"
                  showIcon
                  className="mb-3"
                  message={t("Possible duplicate of {{ref}}", {
                    ref: beleg.duplicateOf.belegNumber || beleg.duplicateOf.invoiceNumber || ""
                  })}
                />
              )}
              {compliance && !compliance.complete && (
                <Alert
                  type="warning"
                  showIcon
                  className="mb-3"
                  message={
                    compliance.kleinbetrag
                      ? t("Missing details (small-amount invoice)")
                      : t("Missing § 14 UStG details")
                  }
                  description={
                    <>
                      <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                        {compliance.missing.map(key => (
                          <li key={key}>{t(MISSING_LABELS[key] || key)}</li>
                        ))}
                      </ul>
                      {!locked && (
                        <Checkbox
                          checked={acknowledge}
                          onChange={e => setAcknowledge(e.target.checked)}
                          style={{ marginTop: 8 }}
                        >
                          {t("I have checked this and want to book it anyway")}
                        </Checkbox>
                      )}
                    </>
                  }
                />
              )}
              {beleg?.bauabzugsteuer?.applies && (
                <Alert
                  type="info"
                  showIcon
                  className="mb-3"
                  message={t("Bauabzugsteuer: {{amount}} (15 %) will be withheld on payment", {
                    amount: formatCents(beleg.bauabzugsteuer.withheld)
                  })}
                />
              )}
              {beleg?.supplier && <BelegSubcontractorAlert belegId={beleg._id} />}
              <CreditPanel beleg={beleg} canEdit={canEdit} onChanged={onCreditChanged} />
              {journal && (
                <Alert
                  type="success"
                  showIcon
                  className="mb-3"
                  message={t("Booked as entry {{no}}/{{year}} on {{date}}", {
                    no: journal.entryNo,
                    year: journal.year,
                    date: journal.date
                  })}
                  description={(journal.lines || []).map((l, i) => (
                    <div key={i}>
                      {l.account}:{" "}
                      <span className="acct-num">
                        {l.debit ? `S ${formatCents(l.debit)}` : `H ${formatCents(l.credit)}`}
                      </span>
                    </div>
                  ))}
                />
              )}

              <Form form={form} layout="vertical" disabled={locked || processing} size={fieldSize}>
                <Row gutter={8}>
                  <Col xs={24} sm={12}>
                    <Form.Item name="kind" label={t("Document type")}>
                      <Select
                        options={Object.entries(KIND_LABELS)
                          .filter(([value]) => value !== "self_billing" || isSelfBilling)
                          .map(([value, label]) => ({
                            value,
                            label: t(label)
                          }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="supplier" label={t("Supplier")}>
                      <Select
                        showSearch
                        allowClear
                        filterOption={false}
                        onSearch={setSupplierSearch}
                        onChange={onSupplierChange}
                        options={supplierOptions}
                        placeholder={t("Choose or type to create")}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={8}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="supplierName"
                      label={t("Supplier name")}
                      rules={[{ required: true, message: t("Required") }]}
                      {...check("supplierName")}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="supplierAddress"
                      label={t("Supplier address")}
                      {...check("supplierAddress")}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={8}>
                  <Col xs={24} sm={8}>
                    <Form.Item name="supplierVatId" label={t("VAT ID")} {...check("supplierVatId")}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="supplierTaxNumber"
                      label={t("Tax number")}
                      {...check("supplierTaxNumber")}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item name="supplierIban" label={t("IBAN")} {...check("supplierIban")}>
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={8}>
                  <Col xs={24} sm={12} xl={6}>
                    <Form.Item
                      name="invoiceNumber"
                      label={t("Invoice number")}
                      {...check("invoiceNumber")}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} xl={6}>
                    <Form.Item
                      name="invoiceDate"
                      label={t("Invoice date")}
                      rules={[{ required: true, message: t("Required") }]}
                      {...check("invoiceDate")}
                    >
                      <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} xl={6}>
                    <Form.Item
                      name="deliveryDate"
                      label={t("Delivery / service date")}
                      {...check("deliveryDate")}
                    >
                      <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} xl={6}>
                    <Form.Item name="dueDate" label={t("Due date")} {...check("dueDate")}>
                      <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                </Row>

                {kind === "credit_note" && (
                  <Form.Item
                    name="referencedInvoiceNumber"
                    label={t("Corrects invoice number")}
                    extra={t("Used to link the credit note to the supplier's invoice.")}
                  >
                    <Input />
                  </Form.Item>
                )}

                <Divider orientation="start" plain>
                  {t("Amounts")}
                </Divider>
                <Form.Item name="taxMode" label={t("Tax treatment")}>
                  <Select
                    options={Object.entries(TAX_MODE_LABELS).map(([value, label]) => ({
                      value,
                      label: t(label)
                    }))}
                    onChange={mode =>
                      form.setFieldsValue({ account: accountFor(category, mode, byRate) })
                    }
                  />
                </Form.Item>
                <Form.List name="byRate">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name }, index) => {
                        // Rows wrap on phones, so every row carries its own labels there.
                        const labelled = index === 0 || isMobile;
                        return (
                          <Row gutter={8} key={key} align="middle">
                            <Col xs={10} sm={5}>
                              <Form.Item
                                name={[name, "rate"]}
                                label={labelled ? t("Tax rate") : ""}
                              >
                                <Select
                                  options={RATES.map(r => ({ value: r, label: `${r} %` }))}
                                  onChange={v => onRateGroupChange(index, "rate", v)}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={14} sm={7}>
                              <Form.Item
                                name={[name, "net"]}
                                label={labelled ? t("Net") : ""}
                                {...(index === 0 ? check("net") : {})}
                              >
                                <MoneyInput
                                  size={fieldSize}
                                  onChange={v => onRateGroupChange(index, "net", v)}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={10} sm={6}>
                              <Form.Item
                                name={[name, "tax"]}
                                label={labelled ? t("Tax") : ""}
                                {...(index === 0 ? check("tax") : {})}
                              >
                                <MoneyInput
                                  size={fieldSize}
                                  onChange={v => onRateGroupChange(index, "tax", v)}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={10} sm={4}>
                              <Form.Item label={labelled ? t("Gross") : ""}>
                                <Amount
                                  cents={(byRate[index]?.net || 0) + (byRate[index]?.tax || 0)}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={4} sm={2}>
                              <Form.Item label={labelled ? " " : ""}>
                                {fields.length > 1 && (
                                  <Button
                                    type="text"
                                    icon={<MinusCircleOutlined />}
                                    aria-label={t("Delete")}
                                    onClick={() => remove(name)}
                                  />
                                )}
                              </Form.Item>
                            </Col>
                          </Row>
                        );
                      })}
                      <Space wrap>
                        <Button
                          size={fieldSize}
                          icon={<PlusOutlined />}
                          onClick={() => add({ rate: 7, net: 0, tax: 0 })}
                        >
                          {t("Add tax rate")}
                        </Button>
                        <Typography.Text strong>
                          {t("Total")}: <span className="acct-num">{formatCents(totals.net)}</span>{" "}
                          + <span className="acct-num">{formatCents(totals.tax)}</span> ={" "}
                          <span className="acct-num">{formatCents(totals.net + totals.tax)}</span>
                        </Typography.Text>
                      </Space>
                    </>
                  )}
                </Form.List>

                <Divider orientation="start" plain>
                  {t("Booking")}
                </Divider>
                <Row gutter={8}>
                  <Col xs={24} sm={12}>
                    <Form.Item name="category" label={t("Category")} {...check("category")}>
                      <Select
                        showSearch
                        optionFilterProp="label"
                        options={categoryOptions}
                        onChange={onCategoryChange}
                        allowClear
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="account" label={t("Account")}>
                      <Select
                        showSearch
                        allowClear
                        optionFilterProp="label"
                        options={(meta?.accounts || []).map(a => ({
                          value: a.number,
                          label: `${a.number} ${a.name}`
                        }))}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={8}>
                  <Col xs={24} sm={12}>
                    <Form.Item name="costCentre" label={t("Cost centre (project)")}>
                      <Select
                        showSearch
                        allowClear
                        optionFilterProp="label"
                        options={projects.map(p => ({ value: p._id, label: p.projectName }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Form.Item name="privateSharePercent" label={t("Private share %")}>
                      <InputNumber min={0} max={100} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Form.Item name="isAsset" label={t("Fixed asset")} valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>
                {isAsset && (
                  <Alert
                    type="info"
                    showIcon
                    className="mb-3"
                    message={t(
                      "Booked to the asset account; depreciation is handled in the asset register."
                    )}
                  />
                )}
                <Row gutter={8}>
                  <Col xs={12} sm={6}>
                    <Form.Item name="skontoPercent" label={t("Skonto %")}>
                      <InputNumber min={0} max={100} step={0.5} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Form.Item name="skontoDays" label={t("Skonto days")}>
                      <InputNumber min={0} max={120} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="purpose" label={t("Purpose")} {...check("purpose")}>
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item name="notes" label={t("Notes")}>
                  <Input.TextArea rows={2} />
                </Form.Item>
              </Form>
            </div>
          )
        })
      )}
      {isSelfBilling && (
        <SelfBillingModal
          open={selfBillingOpen}
          belegId={belegId}
          onClose={() => setSelfBillingOpen(false)}
          onDone={() => {
            setSelfBillingOpen(false);
            load();
            onChanged?.(null);
          }}
        />
      )}
    </Drawer>
  );
};

export default BelegDrawer;
